import type { Locale } from "@tutor/protocol";
import { useEffect, useState, type SyntheticEvent, type ReactNode } from "react";
import * as api from "./api";
import { commonErrorKey, needsFreshSignIn } from "./errors";
import { familyNoticeUrl } from "./notice";
import { Message, Screen } from "./Screen";
import type { DeviceRecord } from "./storage";
import type { AuthStringKey, Translate } from "./strings";

// Set-up by a parent on the child's device (ADR-0018, ADR-0019): email code, invite
// (alpha), children and consent, PINs, then enrollment, which ends the parent session.

type Step =
  | { readonly kind: "email" }
  | { readonly kind: "code" }
  | { readonly kind: "loading" }
  | { readonly kind: "invite" }
  | { readonly kind: "children" }
  | { readonly kind: "addChild" }
  | { readonly kind: "pins"; readonly queue: readonly api.ParentProfile[] }
  | { readonly kind: "enroll" };

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface ParentSetupProps {
  readonly t: Translate;
  readonly locale: Locale;
  readonly onEnrolled: (device: DeviceRecord) => void;
  readonly onCancel: () => void;
}

export function ParentSetup({ t, locale, onEnrolled, onCancel }: ParentSetupProps) {
  const [step, setStep] = useState<Step>({ kind: "email" });
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState<{ key: AuthStringKey; tone?: "info" }>();
  const [busy, setBusy] = useState(false);
  const [me, setMe] = useState<api.Me>({ profiles: [] });
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  /** Children added during this set-up: they must get a PIN. */
  const [added, setAdded] = useState<ReadonlySet<string>>(new Set());

  const go = (next: Step, note?: { key: AuthStringKey; tone?: "info" }) => {
    setStep(next);
    setMessage(note);
  };

  const fail = (error: api.ApiError, specific?: AuthStringKey) => {
    if (needsFreshSignIn(error)) {
      go({ kind: "email" }, { key: "parent.sessionExpired" });
      return;
    }
    setMessage({ key: specific ?? commonErrorKey(error) });
  };

  /** Runs one API step with the screen disabled. */
  const run = async (action: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    try {
      await action();
    } finally {
      setBusy(false);
    }
  };

  const loadMe = async (after?: { addedId: string }) => {
    const res = await api.getMe();
    if (!res.ok) {
      go({ kind: "email" });
      fail(res.error);
      return;
    }
    setMe(res.value);
    if (res.value.alpha?.invited === false) {
      go({ kind: "invite" });
      return;
    }
    if (res.value.profiles.length === 0) {
      go({ kind: "addChild" });
      return;
    }
    setSelected((current) => {
      const ids = new Set(res.value.profiles.map((p) => p.id));
      // First load: every child is selected. Later: keep the choice, add the new child.
      const next = new Set(current.size === 0 ? ids : [...current].filter((id) => ids.has(id)));
      if (after !== undefined) next.add(after.addedId);
      return next;
    });
    go({ kind: "children" });
  };

  const sendCode = (event?: SyntheticEvent) =>
    run(async () => {
      event?.preventDefault();
      const address = email.trim();
      if (!EMAIL.test(address)) {
        setMessage({ key: "parent.invalidEmail" });
        return;
      }
      const res = await api.sendCode(address);
      if (!res.ok) {
        fail(res.error);
        return;
      }
      go(
        { kind: "code" },
        step.kind === "code" ? { key: "parent.codeSent", tone: "info" } : undefined,
      );
    });

  const cancel = () => {
    // Never leave the tablet signed in as a parent. Best effort: offline, the session
    // simply expires.
    void api.signOut();
    onCancel();
  };

  const cancelButton = (
    <button type="button" className="auth-link" onClick={cancel} disabled={busy}>
      {t("common.cancel")}
    </button>
  );

  switch (step.kind) {
    case "email":
      return (
        <Screen title={t("parent.emailTitle")} body={t("parent.emailBody")} footer={cancelButton}>
          <form className="auth-form" onSubmit={(e) => void sendCode(e)} noValidate>
            <label className="auth-label">
              {t("parent.emailLabel")}
              <input
                className="auth-input"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
              />
            </label>
            <Message
              text={message === undefined ? undefined : t(message.key)}
              tone={message?.tone}
            />
            <button className="auth-button auth-button-primary" type="submit" disabled={busy}>
              {t("parent.sendCode")}
            </button>
          </form>
        </Screen>
      );

    case "code":
      return (
        <CodeStep
          t={t}
          email={email.trim()}
          busy={busy}
          message={message}
          footer={cancelButton}
          onResend={() => void sendCode()}
          onBack={() => {
            go({ kind: "email" });
          }}
          onSubmit={(otp) =>
            void run(async () => {
              const res = await api.signInWithCode(email.trim(), otp);
              if (!res.ok) {
                const expired =
                  res.error.kind === "http" &&
                  (res.error.code === "OTP_EXPIRED" || res.error.code === "TOO_MANY_ATTEMPTS");
                setMessage({
                  key:
                    res.error.kind === "http"
                      ? expired
                        ? "parent.codeExpired"
                        : "parent.wrongCode"
                      : commonErrorKey(res.error),
                });
                return;
              }
              go({ kind: "loading" });
              await loadMe();
            })
          }
        />
      );

    case "loading":
      return <Screen title={t("common.loading")} />;

    case "invite":
      return (
        <TextStep
          t={t}
          title={t("invite.title")}
          body={t("invite.body")}
          label={t("invite.label")}
          submit={t("invite.submit")}
          initial=""
          autoCapitalize="characters"
          busy={busy}
          message={message}
          footer={cancelButton}
          onSubmit={(code) =>
            void run(async () => {
              const res = await api.redeemInvite(code);
              if (!res.ok) {
                // A 404 means the API has no invite route: a generic error, not "wrong code".
                const invalid =
                  res.error.kind === "http" && res.error.status >= 400 && res.error.status < 404;
                const mismatch =
                  res.error.kind === "http" && res.error.code === "invite_email_mismatch";
                fail(
                  res.error,
                  mismatch ? "invite.emailMismatch" : invalid ? "invite.invalid" : undefined,
                );
                return;
              }
              await loadMe();
            })
          }
        />
      );

    case "children":
      return (
        <Screen title={t("children.title")} body={t("children.body")} footer={cancelButton}>
          <fieldset className="auth-fieldset">
            <legend className="auth-visually-hidden">{t("children.title")}</legend>
            {me.profiles.map((p) => (
              <label key={p.id} className="auth-check">
                <input
                  type="checkbox"
                  checked={selected.has(p.id)}
                  onChange={(e) => {
                    const next = new Set(selected);
                    if (e.target.checked) next.add(p.id);
                    else next.delete(p.id);
                    setSelected(next);
                  }}
                />
                <span>{p.displayName}</span>
              </label>
            ))}
          </fieldset>
          <Message text={message === undefined ? undefined : t(message.key)} tone={message?.tone} />
          <div className="auth-actions">
            <button
              type="button"
              className="auth-button"
              onClick={() => {
                go({ kind: "addChild" });
              }}
            >
              {t("children.add")}
            </button>
            <button
              type="button"
              className="auth-button auth-button-primary"
              onClick={() => {
                const queue = me.profiles.filter((p) => selected.has(p.id));
                if (queue.length === 0) setMessage({ key: "children.noneSelected" });
                else go({ kind: "pins", queue });
              }}
            >
              {t("common.continue")}
            </button>
          </div>
        </Screen>
      );

    case "addChild":
      return (
        <AddChildStep
          t={t}
          locale={locale}
          busy={busy}
          canGoBack={me.profiles.length > 0}
          footer={cancelButton}
          onBack={() => {
            go({ kind: "children" });
          }}
          onError={fail}
          onSubmit={(input) =>
            void run(async () => {
              const res = await api.createProfile(input);
              if (!res.ok && res.error.kind === "http" && res.error.code === "invite_required") {
                go({ kind: "invite" });
                return;
              }
              if (!res.ok) {
                fail(
                  res.error,
                  res.error.kind === "http" && res.error.status === 400
                    ? "addChild.invalid"
                    : undefined,
                );
                return;
              }
              const addedId = res.value.id;
              setAdded((current) => new Set([...current, addedId]));
              await loadMe({ addedId });
            })
          }
          message={message}
        />
      );

    case "pins": {
      const [child, ...rest] = step.queue;
      if (child === undefined) return null;
      const next = () => {
        go(rest.length === 0 ? { kind: "enroll" } : { kind: "pins", queue: rest });
      };
      return (
        <SetPinStep
          key={child.id}
          t={t}
          name={child.displayName}
          busy={busy}
          canSkip={!added.has(child.id)}
          message={message}
          footer={cancelButton}
          onSkip={next}
          onInvalid={(key) => {
            setMessage({ key });
          }}
          onSubmit={(pin) =>
            void run(async () => {
              const res = await api.setPin(child.id, pin);
              if (!res.ok) {
                const parentCode =
                  res.error.kind === "http" && res.error.code === "same_as_parent_code";
                fail(res.error, parentCode ? "pin.sameAsParentCode" : undefined);
                return;
              }
              next();
            })
          }
        />
      );
    }

    case "enroll":
      return (
        <TextStep
          t={t}
          title={t("enroll.title")}
          body={t("enroll.body")}
          label={t("enroll.label")}
          submit={t("enroll.submit")}
          initial={t("enroll.defaultLabel")}
          busy={busy}
          message={message}
          footer={cancelButton}
          onSubmit={(label) =>
            void run(async () => {
              const profileIds = me.profiles.filter((p) => selected.has(p.id)).map((p) => p.id);
              const res = await api.enrollDevice(label.slice(0, 40), profileIds);
              if (!res.ok) {
                fail(res.error);
                return;
              }
              onEnrolled({ deviceId: res.value.deviceId, credential: res.value.credential });
            })
          }
        />
      );
  }
}

interface StepMessage {
  readonly key: AuthStringKey;
  readonly tone?: "info";
}

function CodeStep(props: {
  t: Translate;
  email: string;
  busy: boolean;
  message: StepMessage | undefined;
  footer: ReactNode;
  onSubmit: (otp: string) => void;
  onResend: () => void;
  onBack: () => void;
}) {
  const { t, message } = props;
  const [otp, setOtp] = useState("");
  return (
    <Screen
      title={t("parent.codeTitle")}
      body={t("parent.codeBody", { email: props.email })}
      footer={props.footer}
    >
      <form
        className="auth-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (/^\d{6}$/.test(otp)) props.onSubmit(otp);
        }}
      >
        <label className="auth-label">
          {t("parent.codeLabel")}
          <input
            className="auth-input auth-input-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={otp}
            onChange={(e) => {
              setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
            }}
          />
        </label>
        <Message text={message === undefined ? undefined : t(message.key)} tone={message?.tone} />
        <button
          className="auth-button auth-button-primary"
          type="submit"
          disabled={props.busy || otp.length !== 6}
        >
          {t("parent.signIn")}
        </button>
        <div className="auth-row">
          <button type="button" className="auth-link" onClick={props.onBack} disabled={props.busy}>
            {t("common.back")}
          </button>
          <button
            type="button"
            className="auth-link"
            onClick={props.onResend}
            disabled={props.busy}
          >
            {t("parent.newCode")}
          </button>
        </div>
      </form>
    </Screen>
  );
}

/** A screen with one text field and a submit button. */
export function TextStep(props: {
  t: Translate;
  title: string;
  body: string;
  label: string;
  submit: string;
  initial: string;
  busy: boolean;
  message: StepMessage | undefined;
  footer?: ReactNode;
  autoCapitalize?: "characters";
  onSubmit: (value: string) => void;
}) {
  const { t, message } = props;
  const [value, setValue] = useState(props.initial);
  return (
    <Screen title={props.title} body={props.body} footer={props.footer}>
      <form
        className="auth-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (value.trim() !== "") props.onSubmit(value.trim());
        }}
      >
        <label className="auth-label">
          {props.label}
          <input
            className="auth-input"
            autoComplete="off"
            autoCapitalize={props.autoCapitalize ?? "sentences"}
            spellCheck={false}
            maxLength={40}
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
            }}
          />
        </label>
        <Message text={message === undefined ? undefined : t(message.key)} tone={message?.tone} />
        <button
          className="auth-button auth-button-primary"
          type="submit"
          disabled={props.busy || value.trim() === ""}
        >
          {props.submit}
        </button>
      </form>
    </Screen>
  );
}

function AddChildStep(props: {
  t: Translate;
  locale: Locale;
  busy: boolean;
  canGoBack: boolean;
  message: StepMessage | undefined;
  footer: ReactNode;
  onBack: () => void;
  onError: (error: api.ApiError) => void;
  onSubmit: (input: {
    displayName: string;
    birthYear: number;
    locale: Locale;
    noticeVersion: string;
  }) => void;
}) {
  const { t } = props;
  const [name, setName] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [childLocale, setChildLocale] = useState<Locale>(props.locale);
  const [consent, setConsent] = useState(false);
  const [version, setVersion] = useState<string>();
  const [local, setLocal] = useState<AuthStringKey>();
  const { onError } = props;

  // The notice version the parent accepts is sent back with the new profile (ADR-0018).
  // Runs once: `onError` only moves the set-up to another step.
  useEffect(() => {
    let live = true;
    void api.getPrivacyNotice().then((res) => {
      if (!live) return;
      if (res.ok) setVersion(res.value.version);
      else onError(res.error);
    });
    return () => {
      live = false;
    };
  }, []);

  const message = local ?? props.message?.key;
  const thisYear = new Date().getFullYear();
  return (
    <Screen title={t("addChild.title")} body={t("addChild.body")} footer={props.footer}>
      <form
        className="auth-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          const year = Number(birthYear);
          const displayName = name.trim();
          if (displayName === "" || !Number.isInteger(year) || year < 2000 || year > thisYear) {
            setLocal("addChild.invalid");
            return;
          }
          if (!consent || version === undefined) {
            setLocal("addChild.consentRequired");
            return;
          }
          setLocal(undefined);
          props.onSubmit({
            displayName,
            birthYear: year,
            locale: childLocale,
            noticeVersion: version,
          });
        }}
      >
        <label className="auth-label">
          {t("addChild.name")}
          <input
            className="auth-input"
            autoComplete="off"
            maxLength={40}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
        </label>
        <label className="auth-label">
          {t("addChild.birthYear")}
          <input
            className="auth-input"
            inputMode="numeric"
            autoComplete="off"
            maxLength={4}
            value={birthYear}
            onChange={(e) => {
              setBirthYear(e.target.value.replace(/\D/g, "").slice(0, 4));
            }}
          />
        </label>
        <fieldset className="auth-fieldset">
          <legend className="auth-label">{t("addChild.language")}</legend>
          {(["en", "fr"] as const).map((l) => (
            <label key={l} className="auth-check">
              <input
                type="radio"
                name="auth-child-locale"
                checked={childLocale === l}
                onChange={() => {
                  setChildLocale(l);
                }}
              />
              <span>{t(l === "en" ? "addChild.english" : "addChild.french")}</span>
            </label>
          ))}
        </fieldset>
        <section className="auth-disclosure" aria-labelledby="auth-ai-title">
          <h2 id="auth-ai-title" className="auth-label">
            {t("addChild.aiTitle")}
          </h2>
          <p>{t("addChild.aiBody")}</p>
          <p>{t("addChild.safetyNote")}</p>
        </section>
        <label className="auth-check auth-consent">
          <input
            type="checkbox"
            checked={consent}
            disabled={version === undefined}
            onChange={(e) => {
              setConsent(e.target.checked);
            }}
          />
          <span>{t("addChild.consent", { version: version ?? "…" })}</span>
        </label>
        <a
          className="auth-link"
          href={familyNoticeUrl(props.locale)}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("addChild.noticeLink")}
        </a>
        <Message text={message === undefined ? undefined : t(message)} />
        <button
          className="auth-button auth-button-primary"
          type="submit"
          disabled={props.busy || version === undefined}
        >
          {t("addChild.submit")}
        </button>
        {props.canGoBack ? (
          <button type="button" className="auth-link" onClick={props.onBack} disabled={props.busy}>
            {t("common.back")}
          </button>
        ) : null}
      </form>
    </Screen>
  );
}

function SetPinStep(props: {
  t: Translate;
  name: string;
  busy: boolean;
  canSkip: boolean;
  message: StepMessage | undefined;
  footer: ReactNode;
  onSubmit: (pin: string) => void;
  onSkip: () => void;
  onInvalid: (key: AuthStringKey) => void;
}) {
  const { t, message } = props;
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const digitsOnly = (value: string) => value.replace(/\D/g, "").slice(0, 6);
  return (
    <Screen
      title={t("pin.setTitle", { name: props.name })}
      body={t("pin.setBody", { name: props.name })}
      footer={props.footer}
    >
      <form
        className="auth-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (!/^\d{4,6}$/.test(pin)) props.onInvalid("pin.invalid");
          else if (pin !== confirm) props.onInvalid("pin.mismatch");
          else props.onSubmit(pin);
        }}
      >
        <label className="auth-label">
          {t("pin.label")}
          <input
            className="auth-input auth-input-code"
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            maxLength={6}
            value={pin}
            onChange={(e) => {
              setPin(digitsOnly(e.target.value));
            }}
          />
        </label>
        <label className="auth-label">
          {t("pin.confirmLabel")}
          <input
            className="auth-input auth-input-code"
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            maxLength={6}
            value={confirm}
            onChange={(e) => {
              setConfirm(digitsOnly(e.target.value));
            }}
          />
        </label>
        <Message text={message === undefined ? undefined : t(message.key)} tone={message?.tone} />
        <button className="auth-button auth-button-primary" type="submit" disabled={props.busy}>
          {t("pin.save")}
        </button>
        {props.canSkip ? (
          <button type="button" className="auth-link" onClick={props.onSkip} disabled={props.busy}>
            {t("pin.skip")}
          </button>
        ) : null}
      </form>
    </Screen>
  );
}
